import type { CalendarEventFeedbackQuestion } from 'shared-types';

type Params = {
  question: CalendarEventFeedbackQuestion;
  values: Record<string, unknown>;
};

type BuiltAnswer = {
  answer: Record<string, unknown>;
  questionId: number;
};

/**
 * Builds the answer the server expects for a single question out of the form
 * values, or null when the question has been left unanswered. Fields follow
 * the naming used by `CalendarEventFeedbackSubmissionQuestion`.
 *
 * A question with nothing filled in is deliberately null rather than an empty
 * answer - the server treats a missing answer as "not answered" and rejects it
 * only if the question is required.
 */
const buildCalendarEventFeedbackAnswer = ({
  question,
  values
}: Params): BuiltAnswer | null => {
  const field = `answer_${question.id}`;

  if (question.type === 'openquestion') {
    const text = ((values[field] as string) || '').trim();

    return text
      ? { answer: { text, type: question.type }, questionId: question.id }
      : null;
  }

  if (question.type === 'starrating') {
    const rating = values[field] as number;

    return rating
      ? { answer: { rating, type: question.type }, questionId: question.id }
      : null;
  }

  if (question.type === 'singlechoice') {
    const optionId = values[field] as string;

    return optionId
      ? {
          answer: { optionIds: [optionId], type: question.type },
          questionId: question.id
        }
      : null;
  }

  const selections = (values[`${field}_options`] as boolean[]) || [];
  const optionIds = question.data.options
    .filter((_, optionIndex) => selections[optionIndex])
    .map((option) => option.id);

  return optionIds.length
    ? { answer: { optionIds, type: question.type }, questionId: question.id }
    : null;
};

export default buildCalendarEventFeedbackAnswer;
